// components/DestinationsModule.js - Updated without stats cards, showing counts instead of percentages
import React from 'react';
import { RefreshCw, Clock, Database, AlertTriangle } from 'lucide-react';

const DestinationsModule = ({
                                uniqueDestinations = 0,
                                topDestinations = [],
                                loading = false,
                                error = null,
                                 lastUpdated = null,
                                 cached = false,
                                 cacheAge = 0,
                                 dataPeriod = null,
                                 trendMode = 'per_range',
                                 autoRefresh = true,
                                 onToggleAutoRefresh = () => {},
                                 onRefresh = () => {},
                                 isStale = false
                             }) => {

    const countLabel = trendMode === 'per_sid' ? 'SIDs' : 'Ranges';
    const title = trendMode === 'per_sid' ? 'Destinations by SID Count' : 'Destinations by Range Count';

    const formatTimeAgo = (date) => {
        if (!date) return 'Never';
        const now = new Date();
        const diffMs = now - new Date(date);
        const diffMins = Math.round(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;

        const diffHours = Math.round(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;

        const diffDays = Math.round(diffHours / 24);
        return `${diffDays}d ago`;
    };

    const getStatusColor = () => {
        if (error) return '#ef4444';
        if (loading) return '#f59e0b';
        if (isStale) return '#f59e0b';
        return '#10b981';
    };

    const getStatusText = () => {
        if (error) return 'Error';
        if (loading) return 'Loading...';
        if (cached && cacheAge > 0) return `Cached (${cacheAge}m)`;
        return 'Live Data';
    };

    return (
        <div style={{
            background: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            borderRadius: '12px',
            padding: '16px',
            gridColumn: '5',
            gridRow: '1 / 4',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: '500px',
            maxHeight: 'none',
            width: '100%',
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.4)'
        }}>
            {/* Header with status */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px'
                    }}>📍</div>
                    <div>
                        <div style={{
                            color: '#e2e8f0',
                            fontSize: '14px',
                            fontWeight: '600'
                        }}>
                            {title}
                            {dataPeriod && (
                                <span style={{
                                    fontSize: '10px',
                                    color: '#64748b',
                                    marginLeft: '8px',
                                    fontWeight: '400'
                                }}>
                                    ({dataPeriod.description})
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Status and controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {/* Status dot */}
                    <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(),
                        animation: loading ? 'pulse 2s infinite' : 'none'
                    }}></div>

                    {/* Status text */}
                    <span style={{
                        fontSize: '9px',
                        color: getStatusColor(),
                        fontWeight: '500'
                    }}>
                        {getStatusText()}
                    </span>

                    {/* Refresh button */}
                    <button
                        onClick={onRefresh}
                        disabled={loading}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#f59e0b',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            padding: '2px',
                            borderRadius: '3px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            opacity: loading ? 0.5 : 1,
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            if (!loading) {
                                e.target.style.backgroundColor = 'rgba(245, 158, 11, 0.1)';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                        }}
                        title="Refresh data"
                    >
                        <RefreshCw
                            size={12}
                            style={{
                                animation: loading ? 'spin 1s linear infinite' : 'none'
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Last updated info */}
            {lastUpdated && (
                <div style={{
                    fontSize: '8px',
                    color: '#64748b',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}>
                    <Clock size={8} />
                    Updated {formatTimeAgo(lastUpdated)}
                </div>
            )}

            {/* Main value */}
            <div style={{
                fontSize: '28px',
                fontWeight: '900',
                color: '#f59e0b',
                marginBottom: '20px', // ✅ Increased margin since we removed stats cards
                textAlign: 'right',
                marginTop: '-6px'
            }}>
                {loading ? '...' : `${uniqueDestinations.toLocaleString()} Destinations`}
            </div>

            {/* ✅ REMOVED: Stats cards section completely */}

            {/* Content */}
            {error ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px',
                    flexDirection: 'column',
                    gap: '12px',
                    color: '#ef4444',
                    flex: '1'
                }}>
                    <AlertTriangle size={32} style={{ opacity: 0.5 }} />
                    <div style={{ fontSize: '12px', textAlign: 'center' }}>
                        Failed to load destinations
                    </div>
                    <button
                        onClick={onRefresh}
                        style={{
                            fontSize: '11px',
                            padding: '6px 12px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '6px',
                            color: '#ef4444',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            ) : loading ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px',
                    flexDirection: 'column',
                    gap: '12px',
                    color: '#f59e0b',
                    flex: '1'
                }}>
                    <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                    <div style={{ fontSize: '12px' }}>Loading destinations...</div>
                </div>
            ) : topDestinations.length === 0 ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px',
                    flexDirection: 'column',
                    gap: '12px',
                    color: '#64748b',
                    flex: '1'
                }}>
                    <Database size={24} style={{ opacity: 0.5 }} />
                    <div style={{ fontSize: '12px' }}>No destinations found</div>
                </div>
            ) : (
                /* ✅ UPDATED: Countries list with larger fonts and count instead of percentage */
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '2px 12px',
                    flex: '1',
                    overflow: 'auto',
                    minHeight: '400px', // ✅ Increased height since we removed stats
                    maxHeight: 'none',
                    padding: '2px 0'
                }}>
                    {topDestinations.slice(0, 20).map((country, index) => (
                        <div
                            key={index}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '6px 0', // ✅ Increased padding for bigger items
                                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                                minHeight: '28px', // ✅ Increased height
                                fontSize: '13px' // ✅ Larger base font size
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.1)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <span style={{
                                color: '#e2e8f0',
                                fontSize: '13px', // ✅ Increased from 11px to 13px
                                fontWeight: '500', // ✅ Increased font weight
                                flex: '1',
                                textAlign: 'left'
                            }}>
                                {country.destination}
                            </span>
                            <span style={{
                                color: '#f59e0b',
                                fontSize: '13px', // ✅ Increased from 11px to 13px
                                fontWeight: '700', // ✅ Increased font weight
                                marginLeft: '8px',
                                minWidth: '50px', // ✅ Increased width for count display
                                textAlign: 'right'
                            }}>
                                {country.count.toLocaleString()} {countLabel}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* Custom scrollbar styles */}
            <style>{`
                .destinations-module .country-list::-webkit-scrollbar {
                    width: 4px;
                }
                
                .destinations-module .country-list::-webkit-scrollbar-track {
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 2px;
                }
                
                .destinations-module .country-list::-webkit-scrollbar-thumb {
                    background: rgba(245, 158, 11, 0.5);
                    border-radius: 2px;
                }
                
                .destinations-module .country-list::-webkit-scrollbar-thumb:hover {
                    background: rgba(245, 158, 11, 0.7);
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
};

export default DestinationsModule;
